import { CaptchaFormMessage } from '@eliadmin/core/src/components/providers/captcha-form-message';
import { useRecaptcha } from '@eliadmin/core/src/components/providers/captcha-provider';
import { FormErrorSummary } from './form-error-summary';
import { Form } from '@eliadmin/core/src/components/ui/form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { z } from 'zod';
import { Field } from './field';
import { useQuery } from '@tanstack/react-query';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCountries } from '@eliadmin/core/src/api/general/general-api';
import Link from '@eliadmin/core/src/components/link';
import { registerStudent } from '@eliadmin/core/src/api/jobportal/jobporta-auth-api';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { useRouter } from 'next/navigation';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { useRef, useState } from 'react';
import { Spinner } from '@eliadmin/core/src/components/ui/spinner';

import { trackUserActions } from '../analytics/analytics-helper';

export const registerUserSchema = (VM: FormValidationMessages) =>
  z
    .object({
      name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Nazov firmy
      surname: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Nazov firmy
      address: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // ulica firmy
      street_number: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // orientacne cislo
      postcode: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Postcode
      city: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Mesto
      country: z.object({
        id: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      }), // Select field, country id
      phone: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()), // Phone number
      title_before: z.string().optional(), // Contact person title before
      title_after: z.string().optional(), // Contact person title after
      different_operation_address: z.boolean(), // Checkbox for different operation address
      description: z.string().optional(), // Description
      categories: z.array(z.string()).optional(), // Categories
      register_email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()), // Register email
      password: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)), // Password
      password_confirmation: z.string().min(8, VM.MIN_LENGTH_VALIDATION_MESSAGE(8)), // Password confirmation
      newsletter: z.boolean().optional(), // Newsletter checkbox
      newsletter_return_url: z.string().optional(), // Newsletter return url
      show_in_index: z.boolean().optional(), // Show in index checkbox
      gdpr: z.boolean().refine(val => val === true, { message: VM.GDPR_VALIDATION_MESSAGE() }), // GDPR checkbox
      vop: z.boolean().refine(val => val === true, {
        message: 'Musíte súhlasiť s VOP.',
      }), // VOP checkbox
      'g-recaptcha-response': z.string().default(''),
    })
    .refine(data => data.password === data.password_confirmation, {
      message: VM.PASSWORD_CONFIRMATION_DONT_MATCH_VALIDATION_MESSAGE(),
      path: ['password_confirmation'],
    });

type RegisterUserSchema = ReturnType<typeof registerUserSchema>;
type RegisterFormUserInputValues = z.input<RegisterUserSchema>;
type RegisterFormUserValues = z.output<RegisterUserSchema>;

export const RegisterFormUser = () => {
  const { t } = useTranslations();
  const locale = useLocaleStore(s => s.currentLocale);
  const { formValidationMessages } = useFormValidationMessages(t);
  const [isLoading, setIsLoading] = useState(false);
  const submitInFlight = useRef(false);
  const { executeRecaptcha } = useRecaptcha();

  const { data: countries } = useQuery({
    queryKey: [CacheKey.COUNTRIES, locale],
    queryFn: () => fetchCountries({ locale }),
    refetchOnWindowFocus: false,
  });

  const form = useForm<RegisterFormUserInputValues, any, RegisterFormUserValues>({
    resolver: zodResolver(registerUserSchema(formValidationMessages)),
    defaultValues: {
      name: '',
      address: '',
      street_number: '',
      postcode: '',
      city: '',
      country: { id: '' },
      phone: '',
      different_operation_address: false,
      register_email: '',
      password: '',
      password_confirmation: '',
      'g-recaptcha-response': '',
      newsletter: false,
      categories: [],
      newsletter_return_url: `${process.env.NEXT_PUBLIC_APP_URL}${ROUTES.NEWSLETTER_CONFIRM()}`,
      show_in_index: true,
      gdpr: false,
      vop: false,
    },
  });

  const { toast } = useToast();
  const router = useRouter();

  const handleSubmit = form.handleSubmit(async data => {
    if (submitInFlight.current) return;
    submitInFlight.current = true;
    setIsLoading(true);
    try {
      const captchaRes = executeRecaptcha ? await executeRecaptcha('register') : '';

      const registerPayload: Parameters<typeof registerStudent>[0] &
        Pick<
          RegisterFormUserValues,
          | 'address'
          | 'street_number'
          | 'postcode'
          | 'city'
          | 'country'
          | 'title_before'
          | 'title_after'
        > = {
        name: data.name,
        surname: data.surname,
        email: data.register_email,
        address: data.address,
        street_number: data.street_number,
        postcode: data.postcode,
        city: data.city,
        country: data.country,
        title_before: data.title_before,
        title_after: data.title_after,
        phone: data.phone || null,
        password: data.password,
        password_confirmation: data.password_confirmation,
        gdpr: data.gdpr,
        newsletter: data.newsletter,
        newsletter_return_url: data.newsletter_return_url || '',
        show_in_index: true,
        tos: data.vop,
        'g-recaptcha-response': captchaRes || '',
        locale,
      };

      const resp = await registerStudent(registerPayload);
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      trackUserActions.registration({
        method: 'form',
        user_type: 'user',
        email: data.register_email,
      });
      toast({
        title: t('text654'),
        variant: 'success',
      });
      router.replace(ROUTES.CURRICULUM_CREATE());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError((field === 'email' ? 'register_email' : field) as any, {
            type: 'manual',
            message: errors.join('\n'),
          });
        }
        return;
      }

      toast({
        title: t('text653'),
        description: (e as any).message,
        variant: 'destructive',
      });
    } finally {
      submitInFlight.current = false;
      setIsLoading(false);
    }
  });

  return (
    <Form {...form}>
      <CaptchaFormMessage errors={form.formState.errors} />
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 md:gap-6">
        <FormErrorSummary errors={form.formState.errors} />
        <div className="flex w-full flex-col gap-4 rounded-lg bg-white p-4 md:gap-[28px] md:p-8">
          <h2 className="text-2xl font-bold md:text-3xl">{t('text198')}</h2>
          <div className="grid grid-cols-1 gap-2 gap-y-4 md:grid-cols-2 md:gap-4 md:gap-y-6">
            <Field
              control={form.control}
              name="title_before"
              label={t('text1726')}
              placeholder={t('text1726') + '...'}
            />
            <Field
              control={form.control}
              name="title_after"
              label={t('text1755')}
              placeholder={t('text1755') + '...'}
            />
          </div>
          <Field
            control={form.control}
            name="name"
            label={t('text33')}
            required
            placeholder={t('text33') + '...'}
          />

          <Field
            control={form.control}
            name="surname"
            label={t('text34')}
            required
            placeholder={t('text34') + '...'}
          />
          <h3 className="text-lg font-bold md:text-2xl">{t('text157')}</h3>
          <div className="grid grid-cols-1 gap-2 gap-y-4 md:grid-cols-2 md:gap-4 md:gap-y-6">
            <Field
              control={form.control}
              name="address"
              label={t('text38')}
              required
              placeholder={t('text38') + '...'}
            />
            <Field
              control={form.control}
              name="street_number"
              label={t('text485')}
              required
              placeholder={t('text485') + '...'}
            />
            <Field
              control={form.control}
              name="city"
              label={t('text39')}
              required
              placeholder={t('text39') + '...'}
            />
            <Field
              control={form.control}
              name="postcode"
              label={t('text40')}
              required
              placeholder={t('text40') + '...'}
            />
          </div>
          <Field
            control={form.control}
            name="country.id"
            type="select"
            options={countries?.data.map(c => ({ value: c.id, label: c.name })) || []}
            label={t('text41')}
          />
        </div>
        <div className="flex w-full flex-col gap-4 rounded-lg bg-white p-4 md:gap-[28px] md:p-8">
          <h2 className="text-2xl font-bold md:text-3xl">{t('text385')}</h2>
          <Field
            control={form.control}
            name="register_email"
            label={t('text35')}
            placeholder={t('text35') + '...'}
            required
          />
          <div className="grid grid-cols-1 gap-2 gap-y-4 md:grid-cols-2 md:gap-4 md:gap-y-6">
            <Field
              control={form.control}
              name="password"
              type="password"
              label={t('text191')}
              required
              placeholder={t('text191') + '...'}
            />
            <Field
              control={form.control}
              name="password_confirmation"
              type="password"
              label={t('text1108')}
              required
              placeholder={t('text1108') + '...'}
            />
          </div>
        </div>
        <div className="flex w-full flex-col gap-4 rounded-lg bg-white p-4 md:gap-[28px] md:p-8">
          <h2 className="text-2xl font-bold md:text-3xl">{t('text360')}</h2>
          <Field
            control={form.control}
            name="phone"
            label={t('text36')}
            required
            type="tel"
            placeholder={t('text36') + '...'}
          />
        </div>
        <div className="flex w-full flex-col gap-4 rounded-lg bg-white p-4 md:gap-[28px] md:p-8">
          <Field
            control={form.control}
            name="newsletter"
            type="checkbox"
            label={t(
              'Súhlasím s odberom mesačného newslettera o trhu práce a emailov s marketingovými informáciami na kontaktnú adresu uvedenú v registračnom formulári.',
            )}
          />
          <Field
            control={form.control}
            name="gdpr"
            type="checkbox"
            label={
              <div>
                {t('text613')}{' '}
                <Link href={ROUTES.GDPR()} target="_blank" className="text-primary">
                  {t('text614')}
                </Link>{' '}
                {t('a pravidlami používania stránky')}
              </div>
            }
          />
          <Field
            control={form.control}
            name="vop"
            type="checkbox"
            label={
              <div>
                Súhlasím so všeobecnými obchodnými podmienkami v zmysle{' '}
                <Link
                  href={'/vseobecne-obchodne-podmienky'}
                  target="_blank"
                  className="text-primary"
                >
                  VOP
                </Link>
                {'.'}
              </div>
            }
          />
        </div>
        <div className="flex w-full justify-center">
          <Button className="w-full md:w-auto" disabled={isLoading}>
            {isLoading ? <Spinner /> : t('text192')}
          </Button>
        </div>
      </form>
    </Form>
  );
};
